
import React, { useState } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Navigation } from '@/components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Building2, Users, ArrowLeft, ChevronRight, Edit, Trash2, Calendar, Shield, CreditCard, UserCheck } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ClubMemberManagement } from '@/components/superadmin/ClubMemberManagement';
import { ClubSeasonManagement } from '@/components/superadmin/ClubSeasonManagement';
import { TeamManagement } from '@/components/admin/TeamManagement';
import { ClubForm } from '@/components/shared/ClubForm';
import { ClubCoachManagement } from '@/components/admin/ClubCoachManagement';
import { PaymentManagementDialog } from '@/components/payment/PaymentManagementDialog';
import { 
  AlertDialog, 
  AlertDialogAction, 
  AlertDialogCancel, 
  AlertDialogContent, 
  AlertDialogDescription, 
  AlertDialogFooter, 
  AlertDialogHeader, 
  AlertDialogTitle 
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

type SportType = 'Baseball' | 'Basketball' | 'Cross Country' | 'Football' | 'Golf' | 'Gymnastics' | 'Ice Hockey' | 'Lacrosse' | 'Soccer' | 'Softball' | 'Swimming' | 'Tennis' | 'Track and Field' | 'Volleyball' | 'Water Polo' | 'Wrestling' | 'Badminton' | 'Bowling' | 'Cheerleading' | 'Dance' | 'Field Hockey' | 'Rowing' | 'Rugby' | 'Skiing' | 'Table Tennis';

const SuperAdminClub = () => {
  const { clubId } = useParams();
  const { profile, loading, isImpersonating, effectiveRole } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [clubName, setClubName] = useState('');
  const [clubLocation, setClubLocation] = useState('');
  const [clubWebsite, setClubWebsite] = useState('');
  const [clubProfilePicture, setClubProfilePicture] = useState('');
  const [clubSport, setClubSport] = useState<SportType | ''>('');

  const { data: club, isLoading: clubLoading } = useQuery({
    queryKey: ['club', clubId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clubs')
        .select(`
          *,
          organizations (
            id,
            name
          )
        `)
        .eq('id', clubId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!clubId,
  });

  // Query for member count
  const { data: memberCount = 0 } = useQuery({
    queryKey: ['club-member-count', clubId],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('user_club_roles')
        .select('*', { count: 'exact', head: true })
        .eq('club_id', clubId)
        .eq('is_active', true);
      
      if (error) throw error;
      return count || 0;
    },
    enabled: !!clubId,
  });

  const deleteClubMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('clubs')
        .delete()
        .eq('id', clubId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Club deleted successfully');
      // Navigate back to the organization page
      if (club?.organizations?.id) {
        navigate(`/superadmin/org/${club.organizations.id}`);
      } else {
        navigate('/superadmin');
      }
    },
    onError: (error) => {
      console.error('Error deleting club:', error);
      toast.error('Failed to delete club');
    },
  });

  const updateClubMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from('clubs')
        .update({ 
          name: clubName,
          website: clubWebsite || null,
          location: clubLocation || null,
          profile_picture_url: clubProfilePicture || null,
          sport: (clubSport as SportType) || null
        })
        .eq('id', clubId);
      
      if (error) throw error;
    },
    onSuccess: async () => {
      // Invalidate and refetch club data instead of reloading the page
      await queryClient.invalidateQueries({ queryKey: ['club', clubId] });
      // Also invalidate the organization's clubs cache to refresh the list
      if (club?.organizations?.id) {
        await queryClient.invalidateQueries({ queryKey: ['organization-clubs', club.organizations.id] });
      }
      toast.success('Club updated successfully');
      setIsEditDialogOpen(false);
    },
    onError: (error) => {
      console.error('Error updating club:', error);
      toast.error('Failed to update club');
    },
  });

  // Redirect if not superadmin or if currently impersonating
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!profile || effectiveRole !== 'superadmin' || isImpersonating) {
    return <Navigate to="/" replace />;
  }

  const handleEdit = () => {
    if (!club) return;
    setClubName(club.name);
    setClubLocation(club.location || '');
    setClubWebsite(club.website || '');
    setClubProfilePicture(club.profile_picture_url || '');
    setClubSport((club.sport as SportType) || '');
    setIsEditDialogOpen(true);
  };

  const handleDelete = () => {
    setDeleteConfirmText('');
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmText === club?.name) {
      deleteClubMutation.mutate();
      setIsDeleteDialogOpen(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!clubName.trim()) return;
    updateClubMutation.mutate();
  };

  if (clubLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6 text-sm text-muted-foreground">
          <Link to="/superadmin" className="hover:text-foreground">
            Super Admin
          </Link>
          <ChevronRight className="h-4 w-4" />
          <Link 
            to={`/superadmin/org/${club?.organizations?.id}`}
            className="hover:text-foreground"
          >
            {club?.organizations?.name}
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground">{club?.name}</span>
        </div>

        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              {club?.sport && (
                <span className="text-sm font-medium px-2 py-1 bg-blue-100 text-blue-800 rounded-md">
                  {club.sport}
                </span>
              )}
              <Avatar className="h-10 w-10">
                <AvatarImage src={club?.profile_picture_url} alt={club?.name} />
                <AvatarFallback>
                  <Building2 className="h-6 w-6 text-green-600" />
                </AvatarFallback>
              </Avatar>
              {club?.name}
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage seasons, members and roles for this club
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleEdit} className="w-full sm:w-auto">
              <Edit className="h-4 w-4 mr-2" />
              Edit Club
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="w-full sm:w-auto">
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Club
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto">
              <Link to={`/superadmin/org/${club?.organizations?.id}`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Organization
              </Link>
            </Button>
          </div>
        </div>

        {/* Club Details */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Club Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Organization</p>
                <p className="text-base">{club?.organizations?.name}</p>
              </div>
              {club?.sport && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Sport</p>
                  <p className="text-base">{club.sport}</p>
                </div>
              )}
              {club?.location && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Location</p>
                  <p className="text-base">{club.location}</p>
                </div>
              )}
              {club?.website && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Website</p>
                  <a 
                    href={club.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Visit Website
                  </a>
                </div>
              )}
            </div>
            
            <div className="mt-6 pt-6 border-t">
              <PaymentManagementDialog
                entityType="club"
                entityId={clubId!}
                entityName={club?.name || ''}
              >
                <Button variant="outline" className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Manage Payments
                </Button>
              </PaymentManagementDialog>
            </div>
          </CardContent>
        </Card>

        {/* Tabbed Management Interface */}
        <Tabs defaultValue="teams" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="teams" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Club Teams
            </TabsTrigger>
            <TabsTrigger value="coaches" className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Coaches
            </TabsTrigger>
            <TabsTrigger value="seasons" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Club Seasons
            </TabsTrigger>
            <TabsTrigger value="members" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Members ({memberCount})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="teams" className="space-y-6">
            {/* Team Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Club Teams
                </CardTitle>
                <CardDescription>
                  Manage teams for this club
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TeamManagement 
                  clubId={clubId!} 
                  organizationId={club?.organizations?.id}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="coaches" className="space-y-6">
            {/* Coach Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="h-5 w-5" />
                  Club Coaches
                </CardTitle>
                <CardDescription>
                  Manage coaches for this club
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ClubCoachManagement 
                  clubId={clubId!} 
                  clubName={club?.name || ''} 
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="seasons" className="space-y-6">
            {/* Season Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Club Seasons
                </CardTitle>
                <CardDescription>
                  Manage seasons for this club
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ClubSeasonManagement clubId={clubId!} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="members">
            {/* Member Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Club Members
                </CardTitle>
                <CardDescription>
                  Manage roles and permissions for club members
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ClubMemberManagement clubId={clubId!} />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Club Dialog */}
        <ClubForm
          isOpen={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          onSubmit={handleSubmit}
          clubName={clubName}
          setClubName={setClubName}
          clubLocation={clubLocation}
          setClubLocation={setClubLocation}
          clubWebsite={clubWebsite}
          setClubWebsite={setClubWebsite}
          clubProfilePicture={clubProfilePicture}
          setClubProfilePicture={setClubProfilePicture}
          clubSport={clubSport}
          setClubSport={setClubSport}
          isSubmitting={updateClubMutation.isPending}
          isEditing={true}
        />

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Club</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete the club "{club?.name}" 
                and all associated data including teams, players, and statistics.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4">
              <Label htmlFor="confirm-name">
                Type the club name "{club?.name}" to confirm deletion:
              </Label>
              <Input
                id="confirm-name"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={club?.name}
                className="mt-2"
              />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                disabled={deleteConfirmText !== club?.name || deleteClubMutation.isPending}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleteClubMutation.isPending ? 'Deleting...' : 'Delete Club'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default SuperAdminClub;
